import { useQueries } from '@tanstack/react-query'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import {
  getTimepointSubmissionStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdGet,
  SubjectsTimepointSchema,
  TimepointSubmissionStatus,
} from '@common/config/api/client'
import { launchDicomViewerForSiteMultipleTimepoints } from '@common/utils/ohifUtils'
import {
  ModalityOption,
  ViewableSubmission,
} from '@features/subject/components/TimePointManager/AllTimepointsViewerSelector'

const VIEWABLE_SUBMISSION_STATUSES: TimepointSubmissionStatus[] = ['COMPLETE', 'LOCKED']
const VIEWABLE_CONTAINER_STATUSES = ['COMPLETE', 'LOCKED', 'FILE_UPLOAD_COMPLETED']

export const getViewableSubmissions = (
  subjectsData: SubjectsTimepointSchema | undefined,
  subjectId?: string,
): ViewableSubmission[] => {
  if (!subjectsData || !subjectId) return []
  return (
    subjectsData.subjects
      ?.find((s) => s.subject_id === subjectId)
      ?.study_timepoints?.flatMap((tp) =>
        (tp.timepoint_submissions ?? []).map((s) => ({
          timepointSubmissionId: s.timepoint_submission_id,
          name: s.name,
          groupName: tp.name,
          isViewable: VIEWABLE_SUBMISSION_STATUSES.includes(s.status as TimepointSubmissionStatus),
        })),
      ) ?? []
  )
}

export const useOpenAllTimepointsInViewer = (
  studyId: string | undefined,
  subjectId: string | undefined,
  viewableSubmissions: ViewableSubmission[],
) => {
  const timepointIds = viewableSubmissions.filter((s) => s.isViewable !== false).map((s) => s.timepointSubmissionId)

  const detailsQueries = useQueries({
    queries: timepointIds.map((tpId) => ({
      queryKey: ['TimePointsDetails', tpId],
      queryFn: () =>
        getTimepointSubmissionStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdGet({
          path: {
            study_id: studyId!,
            subject_id: subjectId!,
            timepoint_submission_id: tpId,
          },
        }),
      enabled: Boolean(studyId && subjectId && tpId),
      staleTime: 5 * 60 * 1000,
    })),
  })

  const allModalitySet = new Set<string>()
  const viewableModalitySet = new Set<string>()
  detailsQueries.forEach((q) => {
    if (q.status !== 'success') return
    const submission = q.data?.data?.timepoint_submission
    const containers = submission?.container_submissions ?? []
    const requirements = submission?.container_requirements ?? []

    requirements.forEach((req) => {
      if (req.container_type === 'DICOM' && req.modality) {
        allModalitySet.add(req.modality)
      }
    })

    containers.forEach((cs) => {
      const req = requirements.find((rq) => rq.container_requirement_id === cs.container_requirement_id)
      if (!req || req.container_type !== 'DICOM') return
      const hasViewableStatus = VIEWABLE_CONTAINER_STATUSES.includes(cs.status ?? '')
      const hasFiles = !cs.no_file_submission_reason && (cs.exam_metadata?.length ?? 0) > 0
      if (hasViewableStatus && hasFiles && req.modality) {
        viewableModalitySet.add(req.modality)
      }
    })
  })
  const modalityOptions: ModalityOption[] = Array.from(allModalitySet)
    .sort()
    .map((name) => ({ name, isViewable: viewableModalitySet.has(name) }))

  const handleOpenAllInViewer = (selectedSubmissionIds: string[], selectedModalities: string[] | null) => {
    if (!studyId || !subjectId || selectedSubmissionIds.length === 0) return

    const pairs: Array<{
      timepointSubmissionId: string
      containerSubmissionId: string
      timepointName: string
    }> = []
    let failed = 0

    detailsQueries.forEach((q, i) => {
      const tpSubmissionId = timepointIds[i]
      if (!selectedSubmissionIds.includes(tpSubmissionId)) return
      if (q.status !== 'success') {
        failed += 1
        return
      }

      const submission = q.data?.data?.timepoint_submission
      const tpName = submission?.name || `Timepoint ${i + 1}`
      const containers = submission?.container_submissions ?? []
      const requirements = submission?.container_requirements ?? []

      containers.forEach((cs) => {
        const req = requirements.find((rq) => rq.container_requirement_id === cs.container_requirement_id)
        if (!req || req.container_type !== 'DICOM') return

        const hasViewableStatus = VIEWABLE_CONTAINER_STATUSES.includes(cs.status ?? '')
        const hasFiles = !cs.no_file_submission_reason && (cs.exam_metadata?.length ?? 0) > 0
        const modalityMatches =
          selectedModalities === null || (req.modality != null && selectedModalities.includes(req.modality))

        if (hasViewableStatus && hasFiles && modalityMatches && cs.container_submission_id) {
          pairs.push({
            timepointSubmissionId: tpSubmissionId,
            containerSubmissionId: cs.container_submission_id,
            timepointName: tpName,
          })
        }
      })
    })

    if (pairs.length === 0) {
      toast(<Toast message="No viewable exams found for the selected timepoints and modalities." variant="info" />)
      return
    }

    if (failed > 0) {
      toast(<Toast message={`Some timepoints could not be loaded (${failed}). Opening the rest.`} variant="warning" />)
    }

    launchDicomViewerForSiteMultipleTimepoints(studyId, subjectId, pairs)
  }

  return { handleOpenAllInViewer, modalityOptions }
}
